import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tooltip } from "primereact/tooltip";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NoInfo from "../../components/NoInfo";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import { clearCahceData, getAllCahceData } from "../../utility/service/cache-service";
import { checkDpSuccess } from "../../utility/service/tool-service";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { FormInput } from "../../components/FormInput";
import { useForm } from "react-hook-form";

const CacheIndex = () => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [columns, setColumns] = useState([]);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [tooltipTarget, setTooltipTarget] = useState(null);
  const isEditable = useEditableFlag('B20105');
  const [filters] = useState({
    value: {
      operator: FilterOperator.OR,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      searchKeyword: ''
    },
    mode: "all"
  });

  const searchKeyword = watch('searchKeyword');

  useEffect(() => {
    axios_getAllCahceData();
  }, []);

  useEffect(() => {
    // 當搜尋關鍵字改變時，自動過濾資料
    filterData();
  }, [searchKeyword, columns]);

  const filterData = () => {
    const keyword = getValues('searchKeyword')?.toLowerCase() || '';
    if (!keyword.trim()) {
      setFilteredColumns(columns);
    } else {
      const filtered = columns.filter(item => 
        item.key?.toLowerCase().includes(keyword) || 
        item.value?.toLowerCase().includes(keyword) ||
        item.createTime?.toLowerCase().includes(keyword) ||
        item.updateTime?.toLowerCase().includes(keyword)
      );
      setFilteredColumns(filtered);
    }
  };

  const onSubmit = (data) => {
    filterData();
  };

  const checkKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      filterData();
    }
  };

  const axios_getAllCahceData = () => {
    checkLoading(loading);
    getAllCahceData()
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody.cacheList;
          setColumns(data);
          setFilteredColumns(data);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const axios_clearCahceData = (reqBody = {}) => {
    checkLoading(loading);
    clearCahceData(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.clear") + " " + t("message.success"));
          axios_getAllCahceData();
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <Button
          tooltip={t("button.clear")}
          tooltipOptions={{ position: "top" }}
          rounded
          text
          severity="secondary"
          className="p-button-icon-only"
          disabled={!isEditable}
          onClick={() => {
            axios_clearCahceData({ key: rowData.key });
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-trash" />
        </Button>
      );
    }
  };

  const rowsPerPageOptions = (dataLength) => {
    const options = [5, 10, 20, 50];
    let result = options.filter((opt) => opt <= dataLength);
    if (!result.includes(dataLength)) {
      result.push(dataLength);
    }
    return result;
  };

  const TooltipBodyTemplate = (rowData, props) => {
    const tooltipContent = rowData?.[props?.field];
    if (rowData) {
      return (
        <span
          ref={(el) => setTooltipTarget(el)}
          data-pr-tooltip={rowData?.[props?.field]}
          data-pr-position={"right"}
          className="nameBody text-ellipsis"
        >
          {tooltipContent}
        </span>
      );
    }
  };

  const ExpandableCell = ({ text, maxLength = 100 }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text) return null;

    const isOverflow = text.length > maxLength;
    const displayText = expanded || !isOverflow ? text : text.substring(0, maxLength) + "...";

    return (
      <div>
        {displayText}
        {isOverflow && (
          <Button
            icon={`${expanded ? "pi pi-chevron-circle-left" : "pi pi-chevron-circle-right"}`}
            className="p-button-text p-button-sm"
            rounded
            text
            onClick={() => setExpanded(!expanded)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {/* 搜尋區域 */}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid">
              <div className="col-12">
                <FormInput
                  register={register}
                  errors={errors}
                  id="searchKeyword"
                  type="text"
                  placeholder={t("keyword")}
                  labelClass="control-label"
                  enterClick={checkKeyDown}
                  labelText={t("keyword")}
                />
              </div>
              <div className="col-12 gap-2 flex-all-center">
                <Button type="submit" label={t("button.query")} size="small" />
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="flex justify-content-end flex-wrap align-items-center">
        <div className="flex items-center gap-2 align-items-end">
          <Button
            tooltip={`${t("button.clear")}${t("plz_chs_all")}`}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            disabled={!isEditable}
            onClick={() => {
              axios_clearCahceData();
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-trash" />
          </Button>
        </div>
      </div>

      <DataTable
        scrollable
        stripedRows
        resizableColumns
        className="white-data-table hover-highlight no-shadow"
        value={filteredColumns}
        emptyMessage={<NoInfo panel="table" />}
        paginator
        rows={5}
        pageLinkSize={3}
        rowsPerPageOptions={rowsPerPageOptions(filteredColumns.length)}
        currentPageReportTemplate={t("pagination_report")}
        paginatorTemplate={{
          layout:
            rowsPerPageOptions(filteredColumns.length).length > 1
              ? "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport "
              : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          RowsPerPageDropdown: (option) => (
            <>
              {t("rowsPerPage")}
              {option.element}
            </>
          ),
        }}
        filterDisplay="menu"
        filters={filters}
      >
        <Column field="key" header="Key" style={{ maxWidth: "300px" }} body={TooltipBodyTemplate} />
        <Column
          field="value"
          header="Value"
          body={(rowData) => <ExpandableCell text={rowData.value} maxLength={300} />}
          style={{ maxWidth: "800px", whiteSpace: "normal", wordBreak: "break-word" }}
          filter
          showFilterMenuOptions={false}
        />
        <Column field="createTime" header={t("create_date")} />
        <Column field="updateTime" header={t("update_date")} />
        <Column field="common" header={t("permanent")} />
        <Column body={actionBodyTemplate} header={t("action")} frozen alignFrozen="right" />
      </DataTable>
      {tooltipTarget && <Tooltip mouseTrack target=".nameBody" event="hover" />}
    </>
  );
};

export default CacheIndex;
